import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import {
  LifeBuoy, Loader2, Send, ArrowLeft, MessageSquare, UserPlus, KeyRound,
  Search, Copy, RefreshCw, CheckCircle2, Phone, Building2,
} from 'lucide-react';
import { Toaster } from '../components/ui/sonner';

const STATUS_LABELS = {
  open: '🟢 Открыт', in_progress: '🔵 В работе', waiting_customer: '🟡 Ждёт клиента',
  resolved: '✅ Решён', closed: '⚫ Закрыт',
};
const STATUS_FLOW = ['open', 'in_progress', 'waiting_customer', 'resolved', 'closed'];

// initData resolution (SDK → URL hash → ?initData query for preview testing).
const INITIAL_HASH = typeof window !== 'undefined' ? window.location.hash : '';
function hashInitData() {
  try {
    for (const h of [INITIAL_HASH, typeof window !== 'undefined' ? window.location.hash : '']) {
      const v = new URLSearchParams((h || '').replace(/^#/, '')).get('tgWebAppData');
      if (v) return v;
    }
  } catch (e) { /* noop */ }
  return '';
}
function queryInitData() {
  try { return new URLSearchParams(window.location.search).get('initData') || ''; } catch (e) { return ''; }
}

function openLink(url) {
  const tg = window.Telegram?.WebApp;
  if (tg?.openLink) tg.openLink(url); else window.open(url, '_blank', 'noopener');
}

function SupportApp() {
  const [initData, setInitData] = useState('');
  const [booting, setBooting] = useState(true);
  const [me, setMe] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [tab, setTab] = useState('tickets');
  const [counts, setCounts] = useState(null);
  const client = axios.create({ baseURL: API, headers: { 'X-Telegram-Init-Data': initData } });

  useEffect(() => {
    let cancelled = false;
    const q = queryInitData();
    if (q) { setInitData(q); setBooting(false); return; }
    const tryBoot = (attempt = 0) => {
      if (cancelled) return;
      const tg = window.Telegram?.WebApp;
      const data = (tg && tg.initData) || hashInitData();
      if (tg) { try { tg.expand(); tg.ready(); } catch (e) {} }
      if (data) { setInitData(data); setBooting(false); return; }
      if (attempt < 12) { setTimeout(() => tryBoot(attempt + 1), 200); return; }
      setBooting(false);
    };
    if (window.Telegram?.WebApp || hashInitData()) { tryBoot(); return; }
    const s = document.createElement('script');
    s.src = 'https://telegram.org/js/telegram-web-app.js';
    s.onload = () => tryBoot();
    s.onerror = () => tryBoot();
    document.head.appendChild(s);
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (booting) return;
    if (!initData) { setAuthError('Откройте приложение внутри Telegram.'); return; }
    client.get('/support-app/me')
      .then(r => setMe(r.data))
      .catch(err => setAuthError(err.response?.status === 403 ? 'Нет доступа. Запросите ссылку у администратора.' : 'Откройте приложение внутри Telegram.'));
    // eslint-disable-next-line
  }, [booting, initData]);

  // Poll ticket counts for the "unread" badge (refreshes every 30s).
  useEffect(() => {
    if (!me) return;
    let alive = true;
    const fetchCounts = () => client.get('/support-app/counts').then(r => { if (alive) setCounts(r.data); }).catch(() => {});
    fetchCounts();
    const id = setInterval(fetchCounts, 30000);
    return () => { alive = false; clearInterval(id); };
    // eslint-disable-next-line
  }, [me, tab]);

  if (authError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6 text-center" data-testid="supportapp-auth-error">
        <div>
          <LifeBuoy className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-700 font-medium">Поддержка Ainanan</p>
          <p className="text-sm text-gray-500 mt-2">{authError}</p>
        </div>
      </div>
    );
  }
  if (!me) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50"><Loader2 className="w-6 h-6 animate-spin text-sky-600" /></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20" data-testid="support-app">
      <Toaster position="top-center" />
      <header className="bg-sky-700 text-white px-4 py-3 sticky top-0 z-10">
        <div className="text-sm opacity-80">Поддержка Ainanan</div>
        <div className="font-semibold">{me.name}</div>
      </header>

      <div className="p-3">
        {tab === 'tickets' && <TicketsTab client={client} />}
        {tab === 'register' && <RegisterTab client={client} />}
        {tab === 'password' && <PasswordTab client={client} />}
      </div>

      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t flex justify-around py-1.5 z-20">
        {[
          ['tickets', 'Тикеты', MessageSquare],
          ['register', 'Клиент', UserPlus],
          ['password', 'Пароль', KeyRound],
        ].map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`relative flex flex-col items-center gap-0.5 px-4 py-1 text-[11px] ${tab === key ? 'text-sky-700' : 'text-gray-400'}`}
            data-testid={`supportapp-nav-${key}`}>
            <span className="relative">
              <Icon className="w-5 h-5" />
              {key === 'tickets' && counts?.unread > 0 && (
                <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center" data-testid="supportapp-unread-badge">
                  {counts.unread > 99 ? '99+' : counts.unread}
                </span>
              )}
            </span>
            {label}
          </button>
        ))}
      </nav>
    </div>
  );
}

// ---------- Tickets ----------
function TicketsTab({ client }) {
  const [tickets, setTickets] = useState(null);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState('open');
  const [selected, setSelected] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('ticket') || null; } catch (e) { return null; }
  });

  const load = () => {
    setTickets(null);
    client.get('/support-app/tickets', { params: { status: statusFilter, limit: 30 } })
      .then(r => { setTickets(r.data.tickets || []); setCounts(r.data.status_counts || {}); })
      .catch(() => { setTickets([]); toast.error('Не удалось загрузить тикеты'); });
  };
  // eslint-disable-next-line
  useEffect(() => { load(); }, [statusFilter]);

  if (selected) {
    return <TicketChat client={client} ticketId={selected} onBack={() => { setSelected(null); load(); }} />;
  }

  return (
    <div className="space-y-2" data-testid="supportapp-tickets">
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {[['open', 'Открытые'], ['in_progress', 'В работе'], ['waiting_customer', 'Ждёт'], ['resolved', 'Решённые'], ['all', 'Все']].map(([v, l]) => (
          <button key={v} onClick={() => setStatusFilter(v)}
            className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap border ${statusFilter === v ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-gray-600'}`}
            data-testid={`supportapp-filter-${v}`}>
            {l}{v !== 'all' && counts[v] != null ? ` ${counts[v]}` : ''}
          </button>
        ))}
      </div>
      {tickets === null ? (
        <Loader2 className="w-5 h-5 animate-spin text-sky-600 mx-auto mt-8" />
      ) : tickets.length === 0 ? (
        <div className="text-center text-gray-400 py-10 text-sm" data-testid="supportapp-tickets-empty">Тикетов нет</div>
      ) : tickets.map(t => (
        <button key={t.id} onClick={() => setSelected(t.id)}
          className="w-full text-left bg-white border rounded-xl p-3" data-testid={`supportapp-ticket-${t.id}`}>
          <div className="flex justify-between items-start gap-2">
            <div className="font-medium text-sm text-gray-900">{t.subject}</div>
            <span className="text-[10px] whitespace-nowrap">{STATUS_LABELS[t.status] || t.status}</span>
          </div>
          <div className="text-[11px] text-gray-500 mt-1">#{t.ticket_number} · {t.user_name || t.user_email || '—'}</div>
        </button>
      ))}
    </div>
  );
}

function TicketChat({ client, ticketId, onBack }) {
  const [t, setT] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const load = () => client.get(`/support-app/tickets/${ticketId}`).then(r => setT(r.data)).catch(() => toast.error('Ошибка загрузки'));
  // eslint-disable-next-line
  useEffect(() => { load(); }, [ticketId]);

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await client.post(`/support-app/tickets/${ticketId}/reply`, { message: reply.trim() });
      setReply('');
      load();
    } catch { toast.error('Не удалось отправить'); }
    finally { setSending(false); }
  };

  const setStatus = async (status) => {
    try { await client.put(`/support-app/tickets/${ticketId}/status`, null, { params: { status } }); load(); toast.success('Статус обновлён'); }
    catch { toast.error('Ошибка'); }
  };

  if (!t) return <Loader2 className="w-5 h-5 animate-spin text-sky-600 mx-auto mt-8" />;

  return (
    <div className="space-y-2" data-testid="supportapp-ticket-chat">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-sky-700" data-testid="supportapp-chat-back">
        <ArrowLeft className="w-4 h-4" /> Назад
      </button>
      <div className="bg-white border rounded-xl p-3">
        <div className="font-semibold text-sm">{t.subject}</div>
        <div className="text-[11px] text-gray-500">#{t.ticket_number} · {t.user_name || t.user_email}{t.user_phone ? ` · ${t.user_phone}` : ''}</div>
        <select value={t.status} onChange={e => setStatus(e.target.value)}
          className="mt-2 text-xs border rounded-lg px-2 py-1.5 w-full" data-testid="supportapp-status-select">
          {STATUS_FLOW.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
      </div>

      <div className="space-y-2" data-testid="supportapp-messages">
        {t.messages.map(m => (
          <div key={m.id} className={`rounded-xl p-2.5 text-sm max-w-[85%] ${m.sender_type === 'customer' ? 'bg-white border' : 'bg-sky-600 text-white ml-auto'}`}>
            <div className={`text-[10px] mb-0.5 ${m.sender_type === 'customer' ? 'text-gray-400' : 'text-sky-100'}`}>{m.sender_name || (m.sender_type === 'customer' ? 'Клиент' : 'Поддержка')}</div>
            {m.message}
          </div>
        ))}
      </div>

      <div className="flex gap-2 sticky bottom-16 bg-gray-50 pt-2">
        <textarea value={reply} onChange={e => setReply(e.target.value)} rows={2} placeholder="Ответ клиенту..."
          className="flex-1 border rounded-lg px-3 py-2 text-sm resize-none" data-testid="supportapp-reply-input" />
        <button onClick={send} disabled={sending} className="bg-sky-600 text-white rounded-lg px-3 flex items-center" data-testid="supportapp-reply-send">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

// ---------- Register client ----------
function RegisterTab({ client }) {
  const [f, setF] = useState({ role: 'B2C', email: '', password: '', full_name: '', phone: '', company_name: '', tax_id: '' });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const genPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let p = '';
    for (let i = 0; i < 10; i++) p += chars[Math.floor(Math.random() * chars.length)];
    p += '!' + Math.floor(Math.random() * 90 + 10);
    setF({ ...f, password: p });
  };

  const submit = async () => {
    if (!f.email.trim() || !f.password.trim()) { toast.error('Email и пароль обязательны'); return; }
    setSaving(true);
    try {
      const r = await client.post('/support-app/clients', f);
      setResult({ email: r.data.email, password: f.password, role: r.data.role });
      toast.success('Клиент создан');
    } catch (e) { toast.error(e.response?.data?.detail || 'Ошибка создания'); }
    finally { setSaving(false); }
  };

  const copy = (txt) => navigator.clipboard.writeText(txt).then(() => toast.success('Скопировано')).catch(() => {});

  if (result) {
    return (
      <div className="bg-white border rounded-xl p-4 space-y-3" data-testid="supportapp-register-result">
        <div className="flex items-center gap-2 text-green-700 font-medium"><CheckCircle2 className="w-5 h-5" /> Клиент создан</div>
        <div className="bg-gray-50 border rounded-lg p-3 text-sm space-y-1">
          <div>Email: <b>{result.email}</b></div>
          <div>Пароль: <b className="font-mono">{result.password}</b></div>
          <div>Роль: {result.role}</div>
        </div>
        <button onClick={() => copy(`Логин: ${result.email}\nПароль: ${result.password}\nВойти: https://ainanan.kz/login`)}
          className="w-full bg-sky-600 text-white rounded-lg py-2 text-sm flex items-center justify-center gap-1" data-testid="supportapp-register-copy">
          <Copy className="w-4 h-4" /> Скопировать доступы
        </button>
        <button onClick={() => { setResult(null); setF({ role: 'B2C', email: '', password: '', full_name: '', phone: '', company_name: '', tax_id: '' }); }}
          className="w-full border rounded-lg py-2 text-sm">Добавить ещё</button>
      </div>
    );
  }

  return (
    <div className="bg-white border rounded-xl p-4 space-y-3" data-testid="supportapp-register">
      <div className="flex rounded-lg border overflow-hidden text-sm">
        {['B2C', 'B2B'].map(r => (
          <button key={r} onClick={() => setF({ ...f, role: r })}
            className={`flex-1 py-2 ${f.role === r ? 'bg-sky-600 text-white' : 'bg-white text-gray-600'}`}
            data-testid={`supportapp-role-${r}`}>{r === 'B2C' ? 'Розница (B2C)' : 'Опт (B2B)'}</button>
        ))}
      </div>
      <Field label="Email" value={f.email} onChange={v => setF({ ...f, email: v })} testid="supportapp-email" type="email" />
      <div>
        <label className="text-xs text-gray-500">Пароль</label>
        <div className="flex gap-2">
          <input value={f.password} onChange={e => setF({ ...f, password: e.target.value })}
            className="flex-1 border rounded-lg px-3 py-2 text-sm font-mono" data-testid="supportapp-password" />
          <button onClick={genPassword} className="border rounded-lg px-3 text-xs" data-testid="supportapp-genpass"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>
      <Field label="ФИО / Контакт" value={f.full_name} onChange={v => setF({ ...f, full_name: v })} testid="supportapp-fullname" />
      <Field label="Телефон" value={f.phone} onChange={v => setF({ ...f, phone: v })} testid="supportapp-phone" />
      {f.role === 'B2B' && <>
        <Field label="Компания" value={f.company_name} onChange={v => setF({ ...f, company_name: v })} testid="supportapp-company" />
        <Field label="БИН/ИИН" value={f.tax_id} onChange={v => setF({ ...f, tax_id: v })} testid="supportapp-taxid" />
      </>}
      <button onClick={submit} disabled={saving} className="w-full bg-sky-600 text-white rounded-lg py-2.5 text-sm flex items-center justify-center gap-1" data-testid="supportapp-register-submit">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4" /> Создать клиента</>}
      </button>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = 'text' }) {
  return (
    <div>
      <label className="text-xs text-gray-500">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
        className="w-full border rounded-lg px-3 py-2 text-sm" data-testid={testid} />
    </div>
  );
}

// ---------- Password reset ----------
function PasswordTab({ client }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);
  const [manual, setManual] = useState('');
  const [resetting, setResetting] = useState(false);
  const [done, setDone] = useState(null);

  const search = async () => {
    if (q.trim().length < 2) { toast.error('Введите минимум 2 символа'); return; }
    setSearching(true); setResults(null);
    try {
      const r = await client.get('/support-app/find-client', { params: { q: q.trim() } });
      setResults(r.data.clients || []);
    } catch { toast.error('Ошибка поиска'); }
    finally { setSearching(false); }
  };

  const doReset = async () => {
    setResetting(true);
    try {
      const body = { client_id: picked.id };
      if (manual.trim()) body.password = manual.trim();
      const r = await client.post('/support-app/reset-password', body);
      setDone({ email: r.data.email, password: r.data.password });
      toast.success('Пароль сброшен');
    } catch (e) { toast.error(e.response?.data?.detail || 'Ошибка сброса'); }
    finally { setResetting(false); }
  };

  const copy = (txt) => navigator.clipboard.writeText(txt).then(() => toast.success('Скопировано')).catch(() => {});
  const waLink = (phone, text) => `https://wa.me/${(phone || '').replace(/\D/g, '')}?text=${encodeURIComponent(text)}`;

  if (done) {
    const creds = `Ваш новый пароль для ainanan.kz\nЛогин: ${done.email}\nПароль: ${done.password}`;
    return (
      <div className="bg-white border rounded-xl p-4 space-y-3" data-testid="supportapp-reset-result">
        <div className="flex items-center gap-2 text-green-700 font-medium"><CheckCircle2 className="w-5 h-5" /> Пароль сброшен</div>
        <div className="bg-gray-50 border rounded-lg p-3 text-sm space-y-1">
          <div>Email: <b>{done.email}</b></div>
          <div>Новый пароль: <b className="font-mono">{done.password}</b></div>
        </div>
        <button onClick={() => copy(creds)} className="w-full bg-sky-600 text-white rounded-lg py-2 text-sm flex items-center justify-center gap-1" data-testid="supportapp-reset-copy">
          <Copy className="w-4 h-4" /> Скопировать текст
        </button>
        {picked?.phone && (
          <button onClick={() => openLink(waLink(picked.phone, creds))} className="w-full bg-green-600 text-white rounded-lg py-2 text-sm flex items-center justify-center gap-1" data-testid="supportapp-reset-whatsapp">
            <Phone className="w-4 h-4" /> Отправить в WhatsApp
          </button>
        )}
        <button onClick={() => { setDone(null); setPicked(null); setResults(null); setQ(''); setManual(''); }} className="w-full border rounded-lg py-2 text-sm">Готово</button>
      </div>
    );
  }

  if (picked) {
    return (
      <div className="bg-white border rounded-xl p-4 space-y-3" data-testid="supportapp-reset-confirm">
        <button onClick={() => setPicked(null)} className="flex items-center gap-1 text-sm text-sky-700"><ArrowLeft className="w-4 h-4" /> Назад</button>
        <div className="text-sm"><b>{picked.company_name || picked.full_name || picked.email}</b><div className="text-xs text-gray-500">{picked.email}{picked.phone ? ` · ${picked.phone}` : ''}</div></div>
        <div>
          <label className="text-xs text-gray-500">Новый пароль (оставьте пустым — сгенерируем)</label>
          <input value={manual} onChange={e => setManual(e.target.value)} placeholder="Автогенерация"
            className="w-full border rounded-lg px-3 py-2 text-sm font-mono" data-testid="supportapp-reset-manual" />
        </div>
        <button onClick={doReset} disabled={resetting} className="w-full bg-sky-600 text-white rounded-lg py-2.5 text-sm flex items-center justify-center gap-1" data-testid="supportapp-reset-submit">
          {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><KeyRound className="w-4 h-4" /> Сбросить пароль</>}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="supportapp-password">
      <div className="flex gap-2">
        <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && search()}
          placeholder="Email, телефон или имя" className="flex-1 border rounded-lg px-3 py-2 text-sm" data-testid="supportapp-search-input" />
        <button onClick={search} className="bg-sky-600 text-white rounded-lg px-3 flex items-center" data-testid="supportapp-search-btn">
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
        </button>
      </div>
      {results === null ? null : results.length === 0 ? (
        <div className="text-center text-gray-400 py-8 text-sm">Клиенты не найдены</div>
      ) : results.map(c => (
        <button key={c.id} onClick={() => setPicked(c)} className="w-full text-left bg-white border rounded-xl p-3" data-testid={`supportapp-client-${c.id}`}>
          <div className="font-medium text-sm flex items-center gap-1">
            {c.role === 'B2B' && <Building2 className="w-3.5 h-3.5 text-gray-400" />}
            {c.company_name || c.full_name || c.email}
          </div>
          <div className="text-[11px] text-gray-500">{c.email}{c.phone ? ` · ${c.phone}` : ''} · {c.role}</div>
        </button>
      ))}
    </div>
  );
}

export default SupportApp;
